import { Col, DatePicker, DatePickerProps, Form, Input, Row, Typography } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FormLabel from "../FormLabel/FormLabel";
import { CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { AiOutlineCalendar } from "react-icons/ai";
import { uniqueId } from "lodash";
import "./CustomFormDatePicker.css";
import { componentPalette } from "src/theme/component-palette";
import { FormatCalendarPicker, FormatDateCalendarPicker } from "src/entity/common/unit.constant";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);
const { Text } = Typography;

interface CustomFormDatePickerProps {
  name: string | (string | number)[];
  label: string;
  customClassName: string;
  disabled?: boolean;
  mode?: "date" | "date-time";
  disabledRanges?: string[];
}

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 0 },
    md: { span: 20, offset: 0 },
  },
};

const CustomFormDatePicker: React.FC<CustomFormDatePickerProps> = ({
  name,
  label,
  customClassName,
  disabled,
  mode = "date-time",
  disabledRanges,
}) => {
  const refDate: any = useRef(null);

  const onOpenCalendar = () => {
    refDate.current?.focus();
    const dateElement: any = document.querySelector("." + customClassName);
    if (dateElement) dateElement?.click();
  };

  const isDisabled = disabled && disabled === true;

  // Parse the ranges into dayjs objects
  const formatDate = mode === "date" ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm";
  const disabledDayjsRanges = useMemo(() => {
    return Array.isArray(disabledRanges)
      ? disabledRanges.map((range) => {
          const [start, end] = range.split(" - ");
          return {
            start,
            end,
          };
        })
      : [];
  }, [disabledRanges]);

  const isRangeOverlap = (newStart: Dayjs, newEnd: Dayjs) => {
    const start = newStart.format(formatDate);
    const end = newEnd.format(formatDate);
    return disabledDayjsRanges.some((item) => item.start === start && item.end === end);
  };

  return (
    <>
      <Form.List name={name}>
        {(fields, { add, remove }, { errors }) => (
          <>
            <FormLabel label={label} />
            {fields?.length === 0 && (
              <div
                style={{
                  borderRadius: 5,
                  borderWidth: 1.5,
                  height: "2.075rem",
                  marginTop: 7.5,
                  width: "100%",
                  backgroundColor: isDisabled ? componentPalette.disabled : "",
                }}
                onClick={isDisabled ? undefined : onOpenCalendar}
              ></div>
            )}
            {fields.map((field, index) => {
              const { key, ...restField } = field;
              return (
                <Form.Item
                  {...formItemLayoutWithOutLabel}
                  className="item-date-picker-group"
                  required={true}
                  key={key + index + uniqueId("item-date-pick")}
                  style={{
                    ...(index === 0
                      ? {
                          borderTopWidth: 1,
                          borderLeftWidth: 1,
                          borderRightWidth: 1,
                          borderTopLeftRadius: 5,
                          borderTopRightRadius: 5,
                          paddingLeft: 7.5,
                          paddingRight: 7.5,
                          paddingTop: 7.5,
                          marginTop: 7.5,
                          ...(fields.length === 1
                            ? { borderBottomWidth: 1, borderBottomLeftRadius: 5, borderBottomRightRadius: 5, paddingBottom: 7.5 }
                            : {}),
                        }
                      : index === fields.length - 1
                        ? {
                            borderBottomWidth: 1,
                            borderLeftWidth: 1,
                            borderRightWidth: 1,
                            borderBottomLeftRadius: 5,
                            borderBottomRightRadius: 5,
                            padding: 7.5,
                          }
                        : { borderLeftWidth: 1, borderRightWidth: 1, paddingRight: 7.5, paddingLeft: 7.5, paddingTop: 7.5 }),
                    ...(isDisabled ? { backgroundColor: componentPalette.disabled } : {}),
                  }}
                >
                  <Row className="w-full">
                    <Col span={24}>
                      <Form.Item {...restField} style={{ width: "100%" }}>
                        <Input style={{ width: "100%" }} disabled />
                      </Form.Item>
                      {fields.length > 0 ? (
                        <CloseCircleOutlined
                          className="dynamic-delete-button"
                          style={{
                            position: "absolute",
                            right: 10,
                            top: 7,
                            zIndex: 109,
                            background: componentPalette.redPeriodDate,
                            height: "60%",
                            paddingLeft: 5,
                          }}
                          onClick={isDisabled ? undefined : () => remove(field.name)}
                        />
                      ) : null}
                    </Col>
                  </Row>
                </Form.Item>
              );
            })}
            <Form.Item>
              <DatePicker.RangePicker
                ref={refDate}
                onChange={(dates, datesString) => {
                  // console.log("datesString :: ", datesString);
                  if (datesString[0] && datesString[1]) {
                    const newStart = dayjs(datesString[0], formatDate);
                    const newEnd = dayjs(datesString[1], formatDate);

                    if (isRangeOverlap(newStart, newEnd)) {
                      // console.log("Is Duplicate start and end date !!");
                      const element: HTMLElement | null = document.querySelector(".date-picker-group span.ant-picker-clear");
                      if (element) {
                        element.click();
                      }
                      // setErrorMessage("Selected date range overlaps with existing ranges.");
                    } else {
                      // setErrorMessage(null); // Clear error message
                      add(`${datesString[0]} - ${datesString[1]}`, 0);
                      const element: HTMLElement | null = document.querySelector(".date-picker-group span.ant-picker-clear");
                      if (element) {
                        element.click();
                      }
                    }
                  }
                }}
                suffixIcon={<CalendarOutlined />}
                format={mode === "date-time" ? FormatCalendarPicker : FormatDateCalendarPicker}
                showTime={mode === "date-time" ? { format: "HH" } : undefined}
                style={{ width: "100%", height: "100%" }}
                disabled={isDisabled}
              />
              <Form.ErrorList errors={errors} />
            </Form.Item>
            <Col
              style={{
                position: "absolute",
                top: fields.length > 0 ? "2.5rem" : "2.25rem",
                right: 10,
                display: "flex",
                alignItems: "center",
                height: fields.length > 0 ? `calc(${fields.length * (fields.length === 1 ? 1.8 : 2.25)}rem)` : "",
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
              onClick={isDisabled ? undefined : onOpenCalendar}
            >
              <VerticalCenter>
                <AiOutlineCalendar size={22}></AiOutlineCalendar>
              </VerticalCenter>
            </Col>
          </>
        )}
      </Form.List>
    </>
  );
};

export default CustomFormDatePicker;
